import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { PathCommand } from '@/lib/elements/types/path-command'
import { pathCommandsForSides } from '@/lib/elements/shape-path-commands'
import {
  innerRatioFromInsetPercent,
  isConvertedToRegularPolygon,
  normInset,
  normSides,
} from '@/lib/object-effects/sides'
import { localVecToCanvas } from '@/lib/transforms/geometry'
import type { Point } from '../types/point'

/** Cubic/quadratic curve flattening resolution — precise enough for a text-wrap boundary; not
 *  adaptive since shape contours here are small, simple polygons/curves, not arbitrary paths. */
export const CURVE_SAMPLE_STEPS = 24

/**
 * Local (0,0..width,height) contour path commands for `shape` — the same resolution order used
 * throughout Object Effects (`effects-panel/helpers/polygon-effect-updates.ts`): an explicit
 * custom path always wins, otherwise regenerate from type/sides/inset at the shape's *current*
 * width/height. Reusing this order means a resized shape's contour is never stale, and every
 * shape kind (including circle/heart, which `pathCommandsForSides` falls back to
 * `shapeToPathCommands` for) is covered by one call.
 */
export function resolveLocalPathCommands(shape: ShapeData): PathCommand[] {
  return (
    shape.pathCommands ??
    pathCommandsForSides(
      shape.type,
      normSides(shape),
      shape.width,
      shape.height,
      innerRatioFromInsetPercent(normInset(shape)),
      { asRegularPolygon: isConvertedToRegularPolygon(shape) }
    )
  )
}

export function cubicPoint(p0: Point, c1: Point, c2: Point, p3: Point, t: number): Point {
  const mt = 1 - t
  const a = mt * mt * mt
  const b = 3 * mt * mt * t
  const c = 3 * mt * t * t
  const d = t * t * t

  return {
    x: a * p0.x + b * c1.x + c * c2.x + d * p3.x,
    y: a * p0.y + b * c1.y + c * c2.y + d * p3.y,
  }
}

export function quadraticPoint(p0: Point, c: Point, p3: Point, t: number): Point {
  const mt = 1 - t

  return {
    x: mt * mt * p0.x + 2 * mt * t * c.x + t * t * p3.x,
    y: mt * mt * p0.y + 2 * mt * t * c.y + t * t * p3.y,
  }
}

/**
 * Flattens moveTo/lineTo/curveTo path commands into a single polygon (line segments only), in
 * the same local coordinate space the commands were authored in. `curveTo` follows the same SVG
 * C (cubic, `controlX2`/`controlY2` or `deltaX`/`deltaY` present) vs Q (quadratic, absent)
 * distinction documented on `CurveToCommand`. The polygon is treated as implicitly closed by
 * callers (last point connects back to the first) — every shape this feeds is a closed fill.
 */
export function flattenPathCommandsToPolygon(commands: PathCommand[]): Point[] {
  const points: Point[] = []
  let current: Point = { x: 0, y: 0 }

  for (const cmd of commands) {
    if ('moveTo' in cmd) {
      current = { x: cmd.moveTo.x, y: cmd.moveTo.y }
      points.push(current)
    } else if ('lineTo' in cmd) {
      current = { x: cmd.lineTo.x, y: cmd.lineTo.y }
      points.push(current)
    } else if ('curveTo' in cmd) {
      const ct = cmd.curveTo
      const p0 = current
      const p3: Point = { x: ct.x, y: ct.y }
      const c1: Point = { x: ct.controlX, y: ct.controlY }
      const c2X = ct.controlX2 ?? ct.deltaX
      const c2Y = ct.controlY2 ?? ct.deltaY
      const isCubic = typeof c2X === 'number' && typeof c2Y === 'number'

      for (let step = 1; step <= CURVE_SAMPLE_STEPS; step++) {
        const t = step / CURVE_SAMPLE_STEPS

        points.push(
          isCubic ? cubicPoint(p0, c1, { x: c2X, y: c2Y }, p3, t) : quadraticPoint(p0, c1, p3, t)
        )
      }

      current = p3
    }
  }

  return points
}

/** Rotates a local shape-space point about the shape's own center by `shape.rotation`, then
 *  translates into canvas space — same convention as `boxVerticesForShapeGeometry`/
 *  `canvasAabbOfRotatedPathBounds` (rotation origin at `x + width/2, y + height/2`). Deliberately
 *  split from translation (see `getShapeFramePolygon`/`getCanvasContourPolygon` below): rotation
 *  is part of the shape's own frame and safe to cache, position is not. */
export function rotateLocalPointInShapeFrame(p: Point, shape: ShapeData): Point {
  const cx = shape.width / 2
  const cy = shape.height / 2
  const rotated = localVecToCanvas({ x: p.x - cx, y: p.y - cy }, shape.rotation ?? 0)

  return { x: cx + rotated.x, y: cy + rotated.y }
}

export type CachedFlatPolygon = { key: string; localPolygon: Point[] }

/** Unrotated, untranslated flattened-contour cache, keyed by shape id — invalidated only when the
 *  shape's OWN geometry changes (type/width/height/sides/inset/custom path). Deliberately excludes
 *  `rotation` (unlike an earlier version of this cache) and `shape.x`/`shape.y`: curve-flattening
 *  (`flattenPathCommandsToPolygon` — 24 samples per Bézier segment, e.g. ~96 points for a circle's
 *  4 cubic arcs) is the genuinely expensive step here and is rotation/position-INDEPENDENT, while
 *  rotating or translating a handful of already-flattened points is cheap enough to redo every call.
 *  Keying the cache on rotation too (as the earlier version did) meant a continuous rotate drag —
 *  where `shape.rotation` changes by a fraction of a degree on every pointer-move tick — invalidated
 *  and re-flattened on EVERY tick, for every wrap-affected text frame near the shape: negligible for
 *  a straight-edged shape (a rectangle's "curve" flatten is just its 4 corners) but real,
 *  visible-as-jitter cost for a circle/heart/inset-star, since flattening runs 60x/sec right when
 *  the drag most needs headroom. Splitting the cache like this keeps the expensive step reusable
 *  across an entire rotate gesture while the rotation applied on top stays exact on every frame — a
 *  pure performance fix, not a behavior change. Deliberately scoped to this module only: it does not
 *  touch or share state with the Bounding Box wrap path or `text-renderer.tsx`'s existing
 *  memoization. */
export const flatPolygonCache = new Map<string, CachedFlatPolygon>()

export const FLAT_POLYGON_CACHE_MAX_ENTRIES = 500

export function unrotatedGeometryKeyForShape(shape: ShapeData): string {
  const pathPart = shape.pathCommands ? JSON.stringify(shape.pathCommands) : ''

  return [
    shape.type,
    shape.width,
    shape.height,
    shape.sides ?? '',
    shape.inset ?? '',
    pathPart,
  ].join('|')
}

export function getLocalFlattenedPolygon(shape: ShapeData): Point[] {
  const key = unrotatedGeometryKeyForShape(shape)
  const cached = flatPolygonCache.get(shape.id)

  if (cached && cached.key === key) return cached.localPolygon

  const localPolygon = flattenPathCommandsToPolygon(resolveLocalPathCommands(shape))

  if (flatPolygonCache.size >= FLAT_POLYGON_CACHE_MAX_ENTRIES) flatPolygonCache.clear()
  flatPolygonCache.set(shape.id, { key, localPolygon })

  return localPolygon
}

export function getShapeFramePolygon(shape: ShapeData): Point[] {
  return getLocalFlattenedPolygon(shape).map((p) => rotateLocalPointInShapeFrame(p, shape))
}

export function getCanvasContourPolygon(shape: ShapeData): Point[] {
  return getShapeFramePolygon(shape).map((p) => ({ x: p.x + shape.x, y: p.y + shape.y }))
}
